import type { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface ActionCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  onClick: () => void;
  isPrimary?: boolean;
}

export function ActionCard({ title, description, icon, onClick, isPrimary = false }: ActionCardProps) {
  // Common cards entrance animation
  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ scale: 1.03, y: -2 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      style={{
        boxShadow: isPrimary
          ? '0 0 20px rgba(124, 58, 237, 0.25), inset 0 0 15px rgba(255,255,255,0.03)'
          : '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 0 10px rgba(255,255,255,0.02)',
      }}
      className={`relative rounded-3xl p-5 md:p-6 cursor-pointer border select-none transition-all duration-300 flex flex-col justify-between group ${
        isPrimary
          ? 'bg-gradient-to-br from-purple-950/40 via-slate-900/60 to-blue-950/40 border-purple-500/40 hover:border-purple-400'
          : 'bg-slate-900/40 backdrop-blur-lg border-white/5 hover:border-white/15'
      }`}
    >
      {/* Primary Badge (e.g. Fastest Badge) */}
      {isPrimary && (
        <span className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white bg-gradient-to-r from-pink-500 to-purple-600 border border-pink-400/30 animate-bounce-gentle">
          Fastest
        </span>
      )}

      {/* SVG Icon Container */}
      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 border ${
          isPrimary
            ? 'bg-purple-500/10 border-purple-500/20 text-purple-400 group-hover:scale-110 shadow-glow'
            : 'bg-white/5 border-white/5 text-surface-300 group-hover:scale-110'
        }`}
      >
        {icon}
      </div>

      {/* Card Info */}
      <div className="flex flex-col text-left">
        <h3
          className={`font-display text-base md:text-lg font-black tracking-wide mb-1 transition-colors ${
            isPrimary ? 'text-purple-300 group-hover:text-white' : 'text-white/95'
          }`}
        >
          {title}
        </h3>
        <p className="text-[11px] md:text-xs text-surface-400 leading-relaxed font-medium">
          {description}
        </p>
      </div>

      {/* Interactive hover glow edge */}
      {isPrimary && (
        <div className="absolute inset-0 rounded-3xl border-2 border-transparent bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none -z-10" />
      )}
    </motion.div>
  );
}
